import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("gmp_token"));
  const [loading, setLoading] = useState(true);
  const [sessionTimeout, setSessionTimeout] = useState(30); // Default 30 minutes
  const lastActivityRef = useRef(Date.now());
  const timeoutCheckRef = useRef(null);

  // Fetch session timeout setting
  const fetchSessionTimeout = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/session-timeout`);
      setSessionTimeout(response.data.timeout_minutes);
    } catch (error) {
      console.error("Failed to fetch session timeout:", error);
    }
  }, []);

  useEffect(() => {
    fetchSessionTimeout();
  }, [fetchSessionTimeout]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Session timeout logic
  useEffect(() => {
    if (!user || !token || sessionTimeout === 0) {
      // Clear any existing timeout check if user is not logged in or timeout is disabled
      if (timeoutCheckRef.current) {
        clearInterval(timeoutCheckRef.current);
        timeoutCheckRef.current = null;
      }
      return;
    }

    // Update last activity on user interaction
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };

    // Listen for user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    // Check for timeout every minute
    timeoutCheckRef.current = setInterval(() => {
      const now = Date.now();
      const lastActivity = lastActivityRef.current;
      const inactiveMinutes = (now - lastActivity) / (1000 * 60);

      if (inactiveMinutes >= sessionTimeout) {
        // Session expired due to inactivity
        toast.warning("Session expired due to inactivity. Please log in again.", {
          duration: 5000
        });
        logout();
      } else if (inactiveMinutes >= sessionTimeout - 2) {
        // Warn user 2 minutes before timeout
        toast.info(`Your session will expire in ${Math.ceil(sessionTimeout - inactiveMinutes)} minute(s) due to inactivity.`, {
          duration: 3000
        });
      }
    }, 60000); // Check every minute

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      if (timeoutCheckRef.current) {
        clearInterval(timeoutCheckRef.current);
      }
    };
  }, [user, token, sessionTimeout]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      lastActivityRef.current = Date.now(); // Reset activity on login
    } catch (error) {
      console.error("Auth error:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, deviceInfo = null) => {
    const payload = { email, password };
    if (deviceInfo) {
      payload.device_fingerprint = deviceInfo.fingerprint;
      payload.device_browser = deviceInfo.browser;
      payload.device_os = deviceInfo.os;
      payload.device_screen = deviceInfo.screen;
    }
    const response = await axios.post(`${API}/auth/login`, payload);
    const { access_token, user: userData } = response.data;
    localStorage.setItem("gmp_token", access_token);
    setToken(access_token);
    setUser(userData);
    lastActivityRef.current = Date.now();
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem("gmp_token", access_token);
    setToken(access_token);
    setUser(userData);
    lastActivityRef.current = Date.now(); // Reset activity on register
    return userData;
  };

  // Google OAuth login — receives JWT token directly from backend redirect
  const loginWithGoogle = async (token) => {
    localStorage.setItem("gmp_token", token);
    setToken(token);
    // Fetch user profile with the new token
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
      lastActivityRef.current = Date.now();
      return response.data;
    } catch (err) {
      localStorage.removeItem("gmp_token");
      setToken(null);
      setUser(null);
      throw err;
    }
  };

  // Initiate Google OAuth flow
  const initiateGoogleLogin = async () => {
    try {
      const response = await axios.get(`${API}/auth/google/login-url`);
      window.location.href = response.data.login_url;
    } catch (error) {
      console.error("Failed to get Google login URL:", error);
    }
  };

  // Microsoft OAuth login
  const loginWithMicrosoft = async (code) => {
    const response = await axios.post(`${API}/auth/microsoft/callback`, { code });
    const { access_token, user: userData, needs_role_selection } = response.data;
    localStorage.setItem("gmp_token", access_token);
    setToken(access_token);
    setUser(userData);
    lastActivityRef.current = Date.now();
    return { ...userData, needs_role_selection };
  };

  // Initiate Microsoft OAuth flow
  const initiateMicrosoftLogin = async () => {
    try {
      const response = await axios.get(`${API}/auth/microsoft/login-url`);
      window.location.href = response.data.login_url;
    } catch (error) {
      console.error("Failed to get Microsoft login URL:", error);
      throw error;
    }
  };

  const logout = async () => {
    // Notify server about logout (for session tracking)
    if (token) {
      try {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error("Logout tracking error:", error);
      }
    }
    localStorage.removeItem("gmp_token");
    setToken(null);
    setUser(null);
  };

  // Refresh session timeout setting (called when admin changes it)
  const refreshSessionTimeout = () => {
    fetchSessionTimeout();
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    loginWithGoogle,
    initiateGoogleLogin,
    loginWithMicrosoft,
    initiateMicrosoftLogin,
    isAuthenticated: !!user,
    isAdmin: user?.user_type === "admin" || user?.user_type === "super_admin" || user?.user_type === "sub_admin" || user?.user_type === "school_admin",
    isSuperAdmin: user?.user_type === "admin" || user?.user_type === "super_admin",
    sessionTimeout,
    refreshSessionTimeout,
    setToken,
    setUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
